import { Banknote, Book, Gift, PawPrint, Star } from 'lucide-preact';
import { useCallback } from 'preact/hooks';

import AppStoreDownloadButton from '@/components/AppStoreDownloadButton';
import BurgerMenu from '@/components/BurgerMenu';
import { LandingSecondary } from '@/components/Buttons';
import NavItem from '@/components/NavBar/NavItem';
import { useShouldDisableJSRouting } from '@/components/providers/DisableJSRoutingProvider';
import SocialNetworkList from '@/components/SocialNetworkList';
import { getHomeURL, isEmbedded } from '@/helpers';
import { useSelector } from '@/redux/helper';
import { goToSignup } from '@/routes/home/helpers';
import { logLandingButton } from '@/shared/ga';

import TalkNavItem from '../TalkNavItem';

const PublicHeaderContent = ({ isLargeScreen, logEvents = false }: { isLargeScreen?: boolean; logEvents?: boolean }) => {
  const giftCardConfig = useSelector((state) => state.remoteConfig.giftCardConfig);
  const shouldDisableJSRouting = useShouldDisableJSRouting();
  const embedded = isEmbedded();

  const origin = window?.location.origin ?? '';
  const target = shouldDisableJSRouting ? '_parent' : embedded ? '_blank' : undefined;
  const rootDomainURL = getHomeURL();

  const onPressSignUp = useCallback(() => {
    goToSignup();
    logLandingButton('Header-Sign-Up');
  }, []);

  const logEvent = useCallback(
    (action: string) => {
      if (logEvents) {
        logLandingButton(action);
      }
    },
    [logEvents],
  );

  return isLargeScreen ? (
    <>
      <NavItem
        avoidInnerPadding
        text="About"
        url={`${rootDomainURL}/about-us`}
        customIcon={<PawPrint className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-About')}
      />
      <NavItem
        avoidInnerPadding
        text="Features"
        url={`${rootDomainURL}/features`}
        customIcon={<Star className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-Features')}
      />
      <NavItem
        avoidInnerPadding
        text="For Educators"
        url={`${rootDomainURL}/educators`}
        customIcon={<Book className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-For-Educators')}
      />
      <TalkNavItem avoidInnerPadding target={target} onClick={() => logEvent('Header-Schedules')} />
      <div className="ml-5 flex items-center justify-center gap-3">
        <NavItem
          avoidInnerPadding
          text="Log in"
          url={`${origin}/login`}
          target={target}
          onClick={() => logEvent('Header-Log-In')}
        />
        <LandingSecondary onClick={onPressSignUp} className="!mr-4 !h-[34px] !w-[102px] !p-0 !text-base !font-bold">
          Sign up
        </LandingSecondary>
      </div>
    </>
  ) : (
    <BurgerMenu>
      <NavItem
        text="About"
        url={`${rootDomainURL}/about-us`}
        customIcon={<PawPrint className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-About')}
        className="text-base"
      />
      <NavItem
        text="Features"
        url={`${rootDomainURL}/features`}
        customIcon={<Star className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-Features')}
        className="text-base"
      />
      <TalkNavItem className="text-base" target={target} onClick={() => logEvent('Header-Schedules')} />
      <NavItem
        text="Pricing"
        url={`${origin}/prices`}
        customIcon={<Banknote className="size-5" />}
        target={target}
        onClick={() => logEvent('Header-Pricing')}
        className="text-base"
      />
      <NavItem
        text="Gift Cards"
        url={`${origin}/gift`}
        onClick={() => logEvent('Header-Gift-Cards')}
        target={target}
        className="text-base"
        customIcon={
          giftCardConfig?.enabled && giftCardConfig?.iconURL ? (
            <img className="preflight preflight-img -mx-0.5 -mt-0.5 size-6" src={giftCardConfig.iconURL} alt="gift box" />
          ) : (
            <Gift className="size-5" />
          )
        }
      />
      <div className="mt-6 flex flex-col items-center gap-6 px-4">
        <AppStoreDownloadButton className="w-full max-w-40" />
        <SocialNetworkList />
        <LandingSecondary onClick={onPressSignUp} className="!h-[52px] !w-full !p-0 !text-base !font-medium">
          Sign up
        </LandingSecondary>
        <a
          href={`${origin}/login`}
          onClick={() => logEvent('Header-Log-In')}
          className="preflight preflight-a text-base font-bold text-grey-3 group-hover:text-grey-1"
        >
          Log in
        </a>
      </div>
    </BurgerMenu>
  );
};

export default PublicHeaderContent;
